import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { env } from '@/config/env'

export interface ApiError {
  code: string
  message: string
  details?: unknown
}

/**
 * Create configured Axios instance with:
 * - Base URL from environment
 * - Auth interceptor to add JWT token
 * - Error handling interceptor
 */
export function createApiClient(getAccessToken: () => string | null): AxiosInstance {
  const client = axios.create({
    baseURL: env.apiBaseUrl,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor: Add auth token (or Basic Auth in bypass mode)
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    if (env.authBypassEnabled) {
      // In bypass mode, API is protected by CloudFront Basic Auth
      const basicAuth = import.meta.env.VITE_API_BASIC_AUTH
      if (basicAuth) {
        config.headers.Authorization = `Basic ${basicAuth}`
      }
    } else {
      const token = getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    return config
  })

  // Response interceptor: Handle errors
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Token expired or invalid - trigger re-auth
        window.dispatchEvent(new CustomEvent('auth:expired'))
      }
      return Promise.reject(error)
    }
  )

  return client
}

/**
 * Standardized error handling for API responses
 */
export function handleApiError(error: AxiosError): ApiError {
  if (!error.response) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to connect. Please check your internet connection.',
    }
  }

  const status = error.response.status

  switch (status) {
    case 400:
      return {
        code: 'VALIDATION_ERROR',
        message: 'Invalid request. Please check your input.',
      }
    case 401:
      return {
        code: 'AUTH_ERROR',
        message: 'Your session has expired. Please log in again.',
      }
    case 403:
      return {
        code: 'FORBIDDEN',
        message: 'You do not have permission to perform this action.',
      }
    case 404:
      return {
        code: 'NOT_FOUND',
        message: 'The requested resource was not found.',
      }
    case 500:
    default:
      return {
        code: 'SERVER_ERROR',
        message: 'Something went wrong. Please try again later.',
      }
  }
}

// Default API client instance (will be configured with auth in AuthContext)
let apiClient: AxiosInstance | null = null

export function getApiClient(): AxiosInstance {
  if (!apiClient) {
    apiClient = createApiClient(() => null)
  }
  return apiClient
}

export function setApiClient(client: AxiosInstance): void {
  apiClient = client
}
